import { Agent, NexusAgent } from '../../../domain/agents/models/Agent';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import { AssistantsChatSession, ChatSession } from '../../../domain/chat/models/ChatSession';
import { ChatDocument } from '../../../domain/documents/models/ChatDocument';
import { resolveSessionAgent } from '../../../domain/resolvers/resolveSessionAgent';
import { isNexusAgent } from '../../utils/AgentUtils';
import { loadChatMessagesHandler } from '../../../application/chat-messages/handlers/LoadChatMessagesHandler';
import { createChatSessionHandler } from '../../../application/chat-sessions/handlers/CreateChatSessionHandler';
import { ProviderServices } from '../../../services/chat/createProviderServices';
import { NexusConversationWriteService } from '../../../services/providers/nexus/services/NexusConversationWriteService';
import { formatTranscript } from '../../utils/formatTranscript';
import { chunkMessages } from '../../utils/chunkMessages';

export type MigrationStepType = 'summarizing' | 'creating' | 'seeding' | 'complete';

export interface MigrationProgress {
    step: MigrationStepType;
    chunkIndex?: number;
    totalChunks?: number;
}

export interface MigrationResult {
    newSession: ChatSession;
    newDocuments: ChatDocument[];
    migrationContext: { migratedFromTitle: string; migrationSummary: string };
}

export interface MigrateChatSessionHandlerParams {
    session: AssistantsChatSession;
    agentsById: Record<string, Agent>;
    accessToken: string;
    providerServices: ProviderServices;
    onProgress: (progress: MigrationProgress) => void;
    signal?: AbortSignal;
}

const checkAbort = (signal?: AbortSignal): void => {
    if (signal?.aborted) throw new DOMException('Migration cancelled by user.', 'AbortError');
};

const SMALL_CHAT_THRESHOLD = 60;
const LARGE_CHAT_THRESHOLD = 241;
const CHUNK_SIZE = 30;
const RECENT_VERBATIM_COUNT = 60;

const invokeAgentAsync = async (
    conversationId: string,
    agentId: string,
    message: string,
    accessToken: string,
    providerServices: ProviderServices,
): Promise<string> => {
    const response = await providerServices.nexus.messages.sendMessageAsync(
        {
            providerType: ChatProviderType.Nexus,
            chatId: conversationId,
            agentId,
            input: message,
        },
        accessToken,
    );
    return response.message ?? '';
};

const deleteTempConversationAsync = async (
    conversationId: string,
    accessToken: string,
    providerServices: ProviderServices,
): Promise<void> => {
    const nexusWrite = providerServices.nexus.sessionWrite as NexusConversationWriteService | undefined;
    await nexusWrite?.hardDeleteChatAsync(conversationId, accessToken).catch((err: unknown) => {
        console.warn(`[Migration] Failed to hard-delete temp conversation ${conversationId}:`, err);
    });
};

const summarizeWithTempConversationAsync = async (
    prompt: string,
    agentId: string,
    accessToken: string,
    providerServices: ProviderServices,
): Promise<string> => {
    const tempResult = await createChatSessionHandler({
        request: { providerType: ChatProviderType.Nexus, agentId, ttlSeconds: 900 },
        accessToken,
        providerServices,
    });
    const tempId = tempResult.session.id;

    try {
        return await invokeAgentAsync(tempId, agentId, prompt, accessToken, providerServices);
    } finally {
        await deleteTempConversationAsync(tempId, accessToken, providerServices);
    }
};

const buildContext = async (
    allMessages: ReturnType<typeof chunkMessages>[0],
    agentId: string,
    accessToken: string,
    providerServices: ProviderServices,
    onProgress: (progress: MigrationProgress) => void,
    signal?: AbortSignal,
): Promise<string> => {
    if (allMessages.length <= SMALL_CHAT_THRESHOLD) {
        return formatTranscript(allMessages);
    }

    const isLarge = allMessages.length >= LARGE_CHAT_THRESHOLD;
    const olderMessages = isLarge ? allMessages.slice(0, allMessages.length - RECENT_VERBATIM_COUNT) : allMessages;
    const recentMessages = isLarge ? allMessages.slice(-RECENT_VERBATIM_COUNT) : [];

    const chunks = chunkMessages(olderMessages, CHUNK_SIZE);

    onProgress({ step: 'summarizing', chunkIndex: 0, totalChunks: chunks.length });
    checkAbort(signal);

    let completedChunks = 0;
    const chunkSummaries = await Promise.all(
        chunks.map(async (chunk) => {
            const chunkTranscript = formatTranscript(chunk);
            const summary = await summarizeWithTempConversationAsync(
                `The following is a segment of a conversation. Extract the key facts, decisions, questions asked and answered, and any action items. Be concise.\n\n${chunkTranscript}`,
                agentId,
                accessToken,
                providerServices,
            );
            completedChunks++;
            onProgress({ step: 'summarizing', chunkIndex: completedChunks, totalChunks: chunks.length });
            return summary;
        }),
    );

    const combinedSummary =
        chunkSummaries.length === 1
            ? chunkSummaries[0]
            : await summarizeWithTempConversationAsync(
                  `The following are summaries of sequential segments of a longer conversation. Merge them into a single coherent context summary preserving all key facts and decisions.\n\n${chunkSummaries.join('\n\n---\n\n')}`,
                  agentId,
                  accessToken,
                  providerServices,
              );

    return recentMessages.length > 0
        ? `${combinedSummary}\n\nRecent messages:\n${formatTranscript(recentMessages)}`
        : combinedSummary;
};

export const migrateChatSessionHandler = async ({
    session,
    agentsById,
    accessToken,
    providerServices,
    onProgress,
    signal,
}: MigrateChatSessionHandlerParams): Promise<MigrationResult> => {
    const primaryMatch = resolveSessionAgent(session, agentsById);
    const resolvedAgent =
        primaryMatch && isNexusAgent(primaryMatch)
            ? primaryMatch
            : (Object.values(agentsById).find(
                  (a): a is NexusAgent =>
                      isNexusAgent(a) &&
                      a.agentId.replace(/\s+/g, '') === session.agentId.replace(/\s+/g, ''),
              ) ?? null);

    if (!resolvedAgent) {
        throw new Error('No matching agent found. Please contact IT support for assistance.');
    }
    const nexusAgent = resolvedAgent;

    const messages = await loadChatMessagesHandler({
        session,
        accessToken,
        providerServices,
        skip: 0,
        count: 9999,
    });
    const contentMessages = messages.filter((m) => m.content?.trim());

    onProgress({ step: 'summarizing' });
    const context = await buildContext(
        contentMessages,
        nexusAgent.agentId,
        accessToken,
        providerServices,
        onProgress,
        signal,
    );

    checkAbort(signal);
    onProgress({ step: 'creating' });

    // System prompt is a short behavioural instruction only — context lives in the seeding message.
    const seedingSystemPrompt =
        `When the user greets you after a migration, respond warmly and mention 2-3 specific topics ` +
        `from the migrated context to confirm you have it. Keep the response to 2-3 sentences.`;

    // Embed the full context in the seeding message so it persists in conversation history for all future turns.
    // The UI strips the __MIGRATION_CTX__ block before rendering, so the user only sees the greeting.
    const seedingMessage =
        `__MIGRATION_CTX_START__\n${context}\n__MIGRATION_CTX_END__\n\n` +
        `Hello! I've just migrated our previous conversation here.`;

    const created = await createChatSessionHandler({
        request: {
            providerType: ChatProviderType.Nexus,
            agentId: nexusAgent.agentId,
            title: session.title,
            systemPrompt: seedingSystemPrompt,
            metadata: {
                migratedFrom: session.id,
                migratedFromTitle: session.title,
                migrationSummary: context,
            },
        },
        accessToken,
        providerServices,
    });
    const newSessionId = created.session.id;

    checkAbort(signal);
    onProgress({ step: 'seeding' });
    try {
        await invokeAgentAsync(
            newSessionId,
            nexusAgent.agentId,
            seedingMessage,
            accessToken,
            providerServices,
        );
    } catch (err) {
        await deleteTempConversationAsync(newSessionId, accessToken, providerServices);
        throw err;
    }

    onProgress({ step: 'complete' });
    return {
        newSession: created.session,
        newDocuments: created.documents,
        migrationContext: { migratedFromTitle: session.title, migrationSummary: context },
    };
};
